import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Chip,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  School as SchoolIcon,
  EmojiEvents as TrophyIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getStudentProgress } from '../../services/teacherService';
import { StudentProgress } from '../../types/teacher';

interface StudentProgressDialogProps {
  open: boolean;
  onClose: () => void;
  studentId: number;
  studentName: string;
}

const StudentProgressDialog: React.FC<StudentProgressDialogProps> = ({
  open,
  onClose,
  studentId,
  studentName
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<StudentProgress | null>(null);

  const fetchProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStudentProgress(studentId);
      setProgress(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load student progress');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (open && studentId) {
      fetchProgress();
    }
  }, [open, studentId, fetchProgress]);

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'warning';
    return 'error';
  };

  const getAccuracyIcon = (accuracy: number) => {
    if (accuracy >= 60) return <TrendingUpIcon />;
    return <TrendingDownIcon />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SchoolIcon />
            <Typography variant="h6">{studentName}'s Progress</Typography>
          </Box>
          <Button onClick={onClose} size="small" startIcon={<CloseIcon />}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && progress && (
          <Box>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'primary.light',
                    borderLeft: 4,
                    borderColor: 'primary.main'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <AssignmentIcon color="primary" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {progress.totalSubmissions}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submissions
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: `${getAccuracyColor(progress.accuracyPercentage)}.light`,
                    borderLeft: 4,
                    borderColor: `${getAccuracyColor(progress.accuracyPercentage)}.main`
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {getAccuracyIcon(progress.accuracyPercentage)}
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {progress.accuracyPercentage.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Accuracy
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'info.light',
                    borderLeft: 4,
                    borderColor: 'info.main'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon color="info" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {progress.totalPoints.toFixed(0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Points
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'warning.light',
                    borderLeft: 4,
                    borderColor: 'warning.main'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrophyIcon color="warning" />
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {progress.achievementsEarned}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Achievements
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Detailed Metrics */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Performance Details
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Correct Submissions
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {progress.correctSubmissions} / {progress.totalSubmissions}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(progress.correctSubmissions / progress.totalSubmissions) * 100}
                  color={getAccuracyColor(progress.accuracyPercentage)}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enrollment Status
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label={`${progress.activeEnrollments} Active Enrollment${progress.activeEnrollments !== 1 ? 's' : ''}`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`${progress.totalEnrollments} Total Enrollment${progress.totalEnrollments !== 1 ? 's' : ''}`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>

              {progress.lastSubmissionDate && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Activity
                  </Typography>
                  <Typography variant="body1">
                    {new Date(progress.lastSubmissionDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
              )}

              {progress.grade && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Grade Level
                  </Typography>
                  <Typography variant="body1">
                    Grade {progress.grade}
                  </Typography>
                </Box>
              )}

              {progress.school && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    School
                  </Typography>
                  <Typography variant="body1">
                    {progress.school}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Recommendations */}
            {progress.accuracyPercentage < 70 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Recommended Actions
                </Typography>
                <Typography variant="body2">
                  {progress.accuracyPercentage < 50
                    ? 'This student is showing significant difficulty and may benefit from one-on-one support or foundational review.'
                    : 'Consider providing additional practice materials or scheduling a check-in to address any challenging topics.'}
                </Typography>
              </Alert>
            )}

            {progress.totalSubmissions === 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  This student hasn't submitted any work yet. Consider reaching out to encourage participation.
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentProgressDialog;
